'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { RenderLink } from '@ui/web/utils'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { LessonType } from '../../../components/lesson-overview-button'
import type { ModuleDetails } from './bottom-nav'
import { LessonOverviewButton } from '../../../components/lesson-overview-button'
import { useSharedTRPC } from '../../../context'
import { BottomNav } from './bottom-nav'
import { TopNav } from './top-nav'

export interface LessonViewPageProps {
  data: ModuleDetails
  userType: 'main' | 'child'
  learnEnrollmentId?: string
  courseId?: string
  getLessonHref: (lessonIndex: number, searchParams?: Record<string, string | undefined>) => string
  onNavigate?: (href: string) => void
  type: 'web' | 'school'
  practicePlayerBackTo?: (enrollmentId: string) => string
  renderBackLink: RenderLink
  /** Passed to player navigation as back_to param */
  backTo?: string
}

// Local lesson entry shape — matches what tRPC returns
interface LessonEntry {
  lesson: {
    id: string
    title: string
    type: string
    guide?: unknown
  } | null
}

type ViewLesson =
  | {
      id: string
      index: number
      title: string
      lessonType: LessonType
      state: 'preview'
      guide: unknown
      onClick: () => void
    }
  | { id: string; index: number; title: string; lessonType: LessonType; state: 'locked' }

function mapLessonType(type: string): LessonType {
  switch (type) {
    case 'READ':
      return 'read'
    case 'QUIZ':
      return 'quiz'
    case 'DISCOVERY':
      return 'discovery'
    case 'IN_CLASS':
      return 'in-class'
    default:
      return 'read'
  }
}

export function LessonViewPage({
  data,
  userType,
  learnEnrollmentId,
  courseId,
  getLessonHref,
  onNavigate,
  type,
  practicePlayerBackTo,
  renderBackLink,
  backTo,
}: LessonViewPageProps) {
  const trpc = useSharedTRPC()
  const topNavRef = useRef<HTMLDivElement>(null)
  const bottomNavRef = useRef<HTMLDivElement>(null)

  const { data: learnProgressData } = useQuery({
    ...trpc.lesson.getAllLearnProgress.queryOptions({ learnEnrollmentId: learnEnrollmentId ?? '' }),
    enabled: !!learnEnrollmentId,
  })

  useEffect(() => {
    const updateNavHeights = () => {
      if (topNavRef.current) {
        document.documentElement.style.setProperty('--top-nav-height', `${topNavRef.current.offsetHeight}px`)
      }
      if (bottomNavRef.current) {
        document.documentElement.style.setProperty('--bottom-nav-height', `${bottomNavRef.current.offsetHeight}px`)
      } else {
        document.documentElement.style.setProperty('--bottom-nav-height', '0px')
      }
    }
    updateNavHeights()
    window.addEventListener('resize', updateNavHeights)
    return () => window.removeEventListener('resize', updateNavHeights)
  }, [])

  const lessons = useMemo((): ViewLesson[] => {
    const rawLessons = data.lessons as LessonEntry[]
    return rawLessons.flatMap((entry, idx): ViewLesson[] => {
      if (!entry.lesson) return []
      const lesson = entry.lesson
      const index = idx + 1
      const lessonType = mapLessonType(lesson.type)

      if (userType === 'main') {
        const onClick = () => {
          const href = getLessonHref(index, backTo ? { back_to: backTo } : undefined)
          onNavigate?.(href)
        }
        return [
          {
            id: lesson.id,
            index,
            title: lesson.title,
            lessonType,
            state: 'preview',
            guide: lesson.guide ?? null,
            onClick,
          },
        ]
      }

      // child: locked, no onClick
      return [{ id: lesson.id, index, title: lesson.title, lessonType, state: 'locked' }]
    })
  }, [data.lessons, userType, getLessonHref, backTo, onNavigate])

  return (
    <main className='srd:w-full srd:h-full srd:min-w-screen srd:min-h-screen srd:relative'>
      {data.backgroundImageUrl && (
        <div className='srd:w-full srd:h-full srd:min-w-screen srd:max-w-screen srd:min-h-screen srd:max-h-screen srd:absolute srd:top-0 srd:left-0 srd:z-0'>
          <div className='srd:absolute srd:inset-0 srd:bg-neutral-black srd:opacity-50 srd:z-10' />
          <img
            src={data.backgroundImageUrl}
            alt={data.title}
            className='srd:w-full srd:h-full srd:object-cover'
            draggable={false}
          />
        </div>
      )}

      <TopNav ref={topNavRef} renderBackLink={renderBackLink} />

      <BottomNav
        ref={bottomNavRef}
        data={data}
        userType={userType}
        learnEnrollmentId={learnEnrollmentId}
        courseId={courseId}
        getLessonHref={getLessonHref}
        learnProgressData={learnProgressData ?? []}
        onNavigate={onNavigate}
        type={type}
        practicePlayerBackTo={practicePlayerBackTo}
      />

      <section
        className={cn(
          'srd:w-full srd:flex srd:items-center srd:justify-start srd:flex-col srd:gap-y-6 srd:z-10 srd:relative srd:px-4 srd:overflow-y-auto',
        )}
        style={{
          height: 'calc(100dvh - var(--top-nav-height, 0px) - var(--bottom-nav-height, 0px))',
          transform: 'translateY(var(--top-nav-height, 0px))',
        }}
      >
        <h5
          className={cn(
            typographyVariants({ variant: 'headline-lg' }),
            'srd:text-neutral-white srd:w-full srd:text-center srd:max-w-[1200px] srd:mx-auto srd:pt-6',
          )}
        >
          {data.title}
        </h5>

        <div className='srd:flex srd:flex-wrap srd:justify-center srd:gap-8 srd:w-full srd:max-w-[800px] srd:mx-auto'>
          {lessons.map((lesson) =>
            lesson.state === 'preview' ? (
              <LessonOverviewButton
                key={lesson.id}
                index={lesson.index}
                title={lesson.title}
                lessonType={lesson.lessonType}
                onClick={lesson.onClick}
                state='preview'
                guide={lesson.guide}
                className='srd:h-[172px]'
              />
            ) : (
              <LessonOverviewButton
                key={lesson.id}
                index={lesson.index}
                title={lesson.title}
                lessonType={lesson.lessonType}
                state='locked'
                className='srd:h-[172px]'
              />
            ),
          )}
        </div>
      </section>
    </main>
  )
}
